import { useEffect, useState } from 'react';
import { Loader2, Newspaper, Plus, Trash2, X, Pencil, ImagePlus, Clock, ExternalLink, Archive, ArchiveRestore } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import RichEditor from '../../components/RichEditor';
import BroadcastToFeedButton from '../../components/BroadcastToFeedButton';

const CATEGORIES = ['Institucional', 'Mercado', 'Capacitación', 'Eventos', 'Legal'];
const HIGHLIGHTS = [
  { value: 'normal', label: 'Normal' },
  { value: 'destacada', label: 'Destacada' },
  { value: 'urgente', label: 'Urgente' },
];

const EMPTY = {
  volanta: '', title: '', excerpt: '', content: '', category: 'Institucional', highlight: 'normal',
  image_url: '', tags: '', reading_minutes: 2, external_source: '', external_url: '', status: 'published',
};

export default function AdminNews() {
  const [articles, setArticles] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [statusTab, setStatusTab] = useState('all');

  const STATUS_TABS = [
    { value: 'all', label: 'Todas' },
    { value: 'published', label: 'Publicadas' },
    { value: 'draft', label: 'Borradores' },
    { value: 'archived', label: 'Archivadas' },
  ];

  const load = () => {
    api
      .get('/admin/news')
      .then(({ data }) => setArticles(data))
      .catch(() => toast.error('No se pudieron cargar las noticias'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setShowForm(true);
  };

  const openEdit = (a) => {
    setEditing(a.article_id);
    setForm({
      volanta: a.volanta || '',
      title: a.title || '',
      excerpt: a.excerpt || '',
      content: a.content || '',
      category: a.category || 'Institucional',
      highlight: a.highlight || 'normal',
      image_url: a.image_url || '',
      tags: (a.tags || []).join(', '),
      reading_minutes: a.reading_minutes || 2,
      external_source: a.external_source || '',
      external_url: a.external_url || '',
      status: a.status || 'published',
    });
    setShowForm(true);
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, image_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
      toast.success('Portada subida.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo subir la portada.');
    } finally {
      setUploadingCover(false);
      e.target.value = '';
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.content || form.content === '<p></p>') {
      toast.error('El cuerpo de la nota no puede estar vacío.');
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title,
      volanta: form.volanta || null,
      excerpt: form.excerpt,
      content: form.content,
      category: form.category,
      highlight: form.highlight,
      image_url: form.image_url || null,
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      reading_minutes: Number(form.reading_minutes) || 2,
      external_source: form.external_source || null,
      external_url: form.external_url || null,
      status: form.status,
    };
    try {
      if (editing) {
        await api.patch(`/admin/news/${editing}`, payload);
        toast.success('Nota actualizada.');
      } else {
        await api.post('/admin/news', payload);
        toast.success(form.status === 'published' ? 'Nota publicada en el portal.' : 'Borrador guardado.');
      }
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar la nota.');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (articleId, status) => {
    try {
      await api.post(`/admin/news/${articleId}/status`, { status });
      toast.success(status === 'archived' ? 'Nota archivada.' : status === 'published' ? 'Nota publicada.' : 'Nota movida a borradores.');
      load();
    } catch {
      toast.error('No se pudo cambiar el estado.');
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('¿Eliminar esta nota del portal?')) return;
    try {
      await api.delete(`/admin/news/${articleId}`);
      toast.success('Nota eliminada.');
      setArticles(articles.filter((a) => a.article_id !== articleId));
    } catch {
      toast.error('No se pudo eliminar.');
    }
  };

  const inputCls =
    'w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15';
  const labelCls = 'mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500';

  const filteredArticles = articles.filter((a) => statusTab === 'all' || (a.status || 'published') === statusTab);
  const countFor = (s) => articles.filter((a) => (a.status || 'published') === s).length;

  return (
    <div className="space-y-6" data-testid="admin-news-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Sala de redacción</p>
          <h1 className="mt-1 flex items-center gap-2 font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">
            <Newspaper className="h-7 w-7 text-cnib-blue" /> Noticias del portal
          </h1>
        </div>
        <button onClick={openNew} data-testid="admin-news-new-button" className="flex items-center gap-2 rounded-full bg-orange-500 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-orange-600">
          <Plus className="h-4 w-4" /> Nueva nota
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSave} className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 md:p-8" data-testid="admin-news-form">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl font-extrabold uppercase tracking-wide text-slate-900">
              {editing ? 'Editar nota' : 'Nueva nota'}
            </h2>
            <button type="button" onClick={() => setShowForm(false)} className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700" data-testid="news-form-close">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <label className={labelCls}>Volanta</label>
              <input value={form.volanta} onChange={(e) => setForm({ ...form, volanta: e.target.value })} className={inputCls} placeholder="Ej. Mercado inmobiliario" data-testid="news-volanta-input" />
            </div>
            <div>
              <label className={labelCls}>Título *</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required minLength={4} className={inputCls} placeholder="Titular de la nota" data-testid="admin-news-title-input" />
            </div>
          </div>

          <div>
            <label className={labelCls}>Bajada</label>
            <textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2} className={inputCls} placeholder="Resumen de una o dos líneas" data-testid="admin-news-excerpt-input" />
          </div>

          <div>
            <label className={labelCls}>Cuerpo *</label>
            <RichEditor value={form.content} onChange={(html) => setForm((f) => ({ ...f, content: html }))} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className={labelCls}>Categoría</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="admin-news-category-select">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Highlight</label>
              <select value={form.highlight} onChange={(e) => setForm({ ...form, highlight: e.target.value })} className={inputCls} data-testid="news-highlight-select">
                {HIGHLIGHTS.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Tags (separados por coma)</label>
              <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className={inputCls} placeholder="vivienda, santa cruz" data-testid="news-tags-input" />
            </div>
            <div>
              <label className={labelCls}>Min de lectura</label>
              <input type="number" min="1" max="120" value={form.reading_minutes} onChange={(e) => setForm({ ...form, reading_minutes: e.target.value })} className={inputCls} data-testid="news-reading-input" />
            </div>
          </div>

          <div>
            <label className={labelCls}>Imagen destacada</label>
            <div className="flex items-center gap-2">
              <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={inputCls} placeholder="https://…" data-testid="admin-news-image-input" />
              <label className={`flex shrink-0 cursor-pointer items-center gap-1.5 rounded-xl bg-cnib-blue px-4 py-2.5 text-xs font-bold text-white ${uploadingCover ? 'opacity-60' : ''}`} data-testid="news-cover-upload">
                {uploadingCover ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
                Subir
                <input type="file" accept="image/*" className="hidden" onChange={uploadCover} disabled={uploadingCover} />
              </label>
            </div>
            {form.image_url && <img src={form.image_url} alt="" className="mt-2 h-24 rounded-xl object-cover" />}
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className={labelCls}>Fuente externa (opcional)</label>
              <input value={form.external_source} onChange={(e) => setForm({ ...form, external_source: e.target.value })} className={inputCls} placeholder="Nombre" data-testid="news-source-name-input" />
            </div>
            <div>
              <label className={labelCls}>URL fuente</label>
              <input value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} className={inputCls} placeholder="https://…" data-testid="news-source-url-input" />
            </div>
            <div>
              <label className={labelCls}>Estado</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls} data-testid="news-status-select">
                <option value="published">Publicada</option>
                <option value="draft">Borrador</option>
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <button type="button" onClick={() => setShowForm(false)} className="rounded-full border border-slate-300 px-6 py-2.5 text-sm font-bold text-slate-600 transition-colors hover:bg-slate-100">
              Cancelar
            </button>
            <button type="submit" disabled={saving} data-testid="admin-news-publish-button" className="flex items-center gap-2 rounded-full bg-orange-500 px-8 py-2.5 text-sm font-bold text-white transition-colors hover:bg-orange-600 disabled:opacity-60">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editing ? 'Guardar cambios' : form.status === 'published' ? 'Publicar' : 'Guardar borrador'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-cnib-blue" /></div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2" data-testid="news-status-tabs">
            {STATUS_TABS.map((t) => (
              <button
                key={t.value}
                onClick={() => setStatusTab(t.value)}
                data-testid={`news-tab-${t.value}`}
                className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${statusTab === t.value ? 'bg-cnib-navy text-white' : 'bg-white text-slate-500 hover:text-slate-800'}`}
              >
                {t.label} {t.value !== 'all' && `(${countFor(t.value)})`}
              </button>
            ))}
          </div>
        <div className="grid gap-4" data-testid="admin-news-list">
          {filteredArticles.length === 0 && (
            <p className="rounded-2xl border border-dashed border-slate-300 py-16 text-center text-sm text-slate-500">
              No hay notas en esta vista.
            </p>
          )}
          {filteredArticles.map((article) => (
            <div key={article.article_id} className="flex items-center gap-5 rounded-2xl border border-slate-200 bg-white p-5" data-testid={`admin-news-item-${article.article_id}`}>
              {article.image_url && <img src={article.image_url} alt="" className="hidden h-16 w-24 rounded-xl object-cover sm:block" />}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-orange-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase text-orange-600">{article.category}</span>
                  {article.highlight === 'urgente' && <span className="rounded-full bg-red-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase text-red-600">Urgente</span>}
                  {article.highlight === 'destacada' && <span className="rounded-full bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase text-amber-600">Destacada</span>}
                  {article.status === 'draft' && <span className="rounded-full bg-slate-200 px-2.5 py-0.5 text-[10px] font-bold uppercase text-slate-500">Borrador</span>}
                  {article.status === 'archived' && <span className="rounded-full bg-purple-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase text-purple-600">Archivada</span>}
                  <span className="flex items-center gap-1 text-xs text-slate-400"><Clock className="h-3 w-3" />{article.reading_minutes || 2} min</span>
                  <span className="text-xs text-slate-400">{new Date(article.created_at).toLocaleDateString('es-BO')}</span>
                </div>
                {article.volanta && <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{article.volanta}</p>}
                <p className="truncate font-heading font-bold text-slate-900">{article.title}</p>
                {article.external_source && (
                  <p className="flex items-center gap-1 text-xs text-slate-400"><ExternalLink className="h-3 w-3" />{article.external_source}</p>
                )}
              </div>
              <button onClick={() => openEdit(article)} data-testid={`admin-news-edit-${article.article_id}`} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cnib-blue/10 text-cnib-blue transition-colors hover:bg-cnib-blue/20" aria-label="Editar nota">
                <Pencil className="h-4 w-4" />
              </button>
              {article.status === 'published' && (
                <BroadcastToFeedButton
                  contentType="news"
                  refId={article.slug || article.article_id}
                  title={article.title}
                  testid={`admin-news-broadcast-${article.article_id}`}
                  size="sm"
                />
              )}
              {article.status === 'archived' ? (
                <button onClick={() => setStatus(article.article_id, 'published')} data-testid={`admin-news-restore-${article.article_id}`} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 transition-colors hover:bg-emerald-500/20" aria-label="Restaurar nota" title="Restaurar (publicar)">
                  <ArchiveRestore className="h-4 w-4" />
                </button>
              ) : (
                <button onClick={() => setStatus(article.article_id, 'archived')} data-testid={`admin-news-archive-${article.article_id}`} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-purple-500/10 text-purple-600 transition-colors hover:bg-purple-500/20" aria-label="Archivar nota" title="Archivar">
                  <Archive className="h-4 w-4" />
                </button>
              )}
              <button onClick={() => handleDelete(article.article_id)} data-testid={`admin-news-delete-${article.article_id}`} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-500/10 text-red-600 transition-colors hover:bg-red-500/20" aria-label="Eliminar nota">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  );
}
